import React, { ReactElement } from "react";

import { FieldArray, useFormikContext } from "formik";
import { IClient } from "../../../../common/interfaces/IClient";
import { canAddAdult, canAddChildren, canDeleteClient, getClientNumberDisplay, numberOfChildren } from "../../../../common/shared/clientHelpers";
import { IQuoteSettings } from "../../../interfaces/form/IQuoteSettings";

//styles
import cx from "classnames";
import styles from "./ClientDetailsSection.module.scss";

//components
import Tooltip from "../../../../common/components/Tooltips/Tooltip";
import useSettingsBuilder from "../../../hooks/useSettingsBuilder";
import ClientChildrenContainer from "./ChildrenClientDetailsSection";
import ClientBox from "../ClientBox/ClientBox";
import ProductSettingsClientWrapper from "../../Products/ProductSettingsClientWrapper/ProductSettingsClientWrapper";

const MAX_ADULTS = 2;

export interface IFamilySettingsForm {
    clients: IClient[];
}

const ClientDetailsSection: React.FC = (): ReactElement => {
    // Hooks
    const { values } = useFormikContext<IQuoteSettings>();

    const { defaultAdult, defaultChild } = useSettingsBuilder();

    return (
        <ProductSettingsClientWrapper className="flex-gap-m">
            <FieldArray name="clients">
                {({ remove, push, name }) => (
                    <React.Fragment>
                        {values.clients
                            .sort((a, b) =>
                                a.isChild === b.isChild ? 0 : a.isChild ? 1 : -1
                            )
                            .map((client, index) => {
                                if (!client.isChild) {
                                    return (
                                        <ClientBox
                                            id={`${name}.${index}`}
                                            key={`${name}-${index}-${client.personId}`}
                                            index={index}
                                            title={
                                                client.isChild
                                                    ? "Children"
                                                    : ` Adult
                                             ${getClientNumberDisplay(
                                                 index,
                                                 values.clients,
                                                 client
                                             )}`
                                            }
                                            client={client}
                                            onDelete={
                                                (client.isChild ||
                                                    canDeleteClient(values.clients)) &&
                                                (() => {
                                                    remove(index);
                                                })
                                            }
                                        />
                                    );
                                }
                            })}
                        {numberOfChildren(values.clients) > 0 && (
                            <ClientChildrenContainer
                                clients={values.clients}
                                onDelete={remove}
                                addChild={() => {
                                    push({
                                        ...defaultChild,
                                        isSelectedDefault: true
                                    });
                                }}
                            />
                        )}

                        {canAddAdult(values.clients) && (
                            <button
                                className={cx(
                                    "panel-card",
                                    "padding-2xl",
                                    "layout-flex-row--centered",
                                    "ga_click",

                                    styles["add-client-button"]
                                )}
                                data-ga_category="build-step"
                                data-ga_action="family"
                                data-ga_label={"add-adult"}
                                onClick={() => {
                                    push({
                                        ...defaultAdult,
                                        isSelectedDefault: true
                                    });
                                }}>
                                <h3
                                    className={cx(
                                        "text-brand",
                                        "text-font-size-l",
                                        "text-zero-margin"
                                    )}>
                                    Add another adult
                                </h3>
                            </button>
                        )}

                        {canAddChildren(values.clients) && (
                            <button
                                className={cx(
                                    "panel-card",
                                    "padding-2xl",
                                    "layout-flex-row--centered",
                                    "ga_click",

                                    styles["add-client-button"]
                                )}
                                data-ga_category="build-step"
                                data-ga_action="family"
                                data-ga_label={"add-child"}
                                onClick={() => {
                                    push({
                                        ...defaultChild,
                                        isSelectedDefault: true
                                    });
                                }}>
                                <h3
                                    className={cx(
                                        "text-brand",
                                        "text-font-size-l",
                                        "text-zero-margin"
                                    )}>
                                    Add children
                                </h3>

                                <Tooltip
                                    className="space-pre-m"
                                    tooltipContent="Children are under the age of 16."
                                />
                            </button>
                        )}
                    </React.Fragment>
                )}
            </FieldArray>
        </ProductSettingsClientWrapper>
    );
};

export default ClientDetailsSection;
